// Settings screen (spec §9) — trip details, categories, currencies, export, reset.

import { iconBadge } from '../lib/icons';
import { state } from '../state';
import { PHOTO_STORAGE_NOTE } from '../lib/db';
import { renderAddButton, renderNav, renderOfflineBanner, escapeHtml } from './shared';

export function renderSettings(): string {
  const t = state.trip;
  return `
  <div class="screen">
    ${renderOfflineBanner()}
    <div class="topbar"><h1>Settings</h1>${renderAddButton()}</div>

    <div class="card">
      <div class="card-title"><h2>Trip details</h2></div>
      <div class="field"><label for="setName">Trip name</label><input id="setName" value="${escapeHtml(t.name)}" onchange="App.updateTripField('name', this.value)"/></div>
      <div class="field"><label for="setDesc">Description</label><textarea id="setDesc" onchange="App.updateTripField('description', this.value)">${escapeHtml(t.description)}</textarea></div>
      <div class="row2">
        <div class="field"><label for="setStart">Start date</label><input type="date" id="setStart" value="${t.startDate}" onchange="App.updateTripField('startDate', this.value)"/></div>
        <div class="field"><label for="setEnd">End date</label><input type="date" id="setEnd" value="${t.endDate}" onchange="App.updateTripField('endDate', this.value)"/></div>
      </div>
      <div class="field"><label for="setBudget">Total budget (${t.baseCurrency})</label><input type="number" min="0" step="1" id="setBudget" value="${t.totalBudget}" onchange="App.updateTripBudget(this.value)"/></div>
      <div class="field"><label>Countries</label>
        <div class="tag-list">${t.countries
          .map((c, i) => `<span class="tag no-ic">${escapeHtml(c)}<button onclick="App.removeCountry(${i})" aria-label="Remove ${escapeHtml(c)}">✕</button></span>`)
          .join('')}</div>
        <div class="inline-add"><label class="visually-hidden" for="newCountry">Add country</label><input id="newCountry" placeholder="Add country…"/><button class="btn btn-ghost btn-sm" onclick="App.addCountry()">Add</button></div>
      </div>
    </div>

    <div class="card">
      <div class="card-title"><h2>Expense categories</h2></div>
      <div class="tag-list">${state.categories
        .map(
          (c) =>
            `<span class="tag">${iconBadge(c.icon, { size: 'icon-badge-sm', round: true, iconSize: 13 })} ${escapeHtml(c.name)}<button onclick="App.removeCategory('${c.id}')" aria-label="Remove ${escapeHtml(c.name)}">✕</button></span>`
        )
        .join('')}</div>
      <div class="inline-add"><label class="visually-hidden" for="newCategory">Add category</label><input id="newCategory" placeholder="Add category…"/><button class="btn btn-ghost btn-sm" onclick="App.addCategory()">Add</button></div>
    </div>

    <div class="card">
      <div class="card-title"><h2>Currencies</h2></div>
      <div class="tag-list">${state.currencies
        .map(
          (c) => `<span class="tag no-ic clickable ${c.code === t.baseCurrency ? 'default' : ''}" role="button" tabindex="0" onclick="App.setBaseCurrency('${c.code}')" onkeydown="if(event.key==='Enter')App.setBaseCurrency('${c.code}')">${c.code} ${
            c.code === t.baseCurrency ? '· base' : ''
          }${c.code !== t.baseCurrency ? `<button onclick="event.stopPropagation(); App.removeCurrency('${c.code}')" aria-label="Remove ${c.code}">✕</button>` : ''}</span>`
        )
        .join('')}</div>
      <div class="inline-add"><label class="visually-hidden" for="newCurrency">Add currency ISO code</label><input id="newCurrency" placeholder="ISO code e.g. CHF" maxlength="3" style="text-transform:uppercase;"/><button class="btn btn-ghost btn-sm" onclick="App.addCurrency()">Add</button></div>
      <div class="budget-note" style="margin-top:10px;">Tap a currency to set it as the trip's base currency. Currencies used by existing expenses can't be removed.</div>
    </div>

    <div class="card">
      <div class="card-title"><h2>Export data</h2></div>
      <div class="export-btns">
        <button class="btn btn-ghost" onclick="App.exportCSV()">${iconBadge('download', { size: 'icon-badge-sm', iconSize: 14 })} CSV</button>
        <button class="btn btn-ghost" onclick="App.exportXLSX()">${iconBadge('download', { size: 'icon-badge-sm', iconSize: 14 })} Excel (.xlsx)</button>
      </div>
    </div>

    <div class="card">
      <div class="card-title"><h2>Your data</h2></div>
      <p class="budget-note" style="margin-top:0;">${PHOTO_STORAGE_NOTE} Everything — trip details, expenses and photos — stays on this device; there's no account and nothing is uploaded anywhere unless you export it yourself.</p>
    </div>

    <div class="card" style="border-color:rgba(240,85,76,.25);">
      <div class="card-title"><h2 style="color:var(--red);">Danger zone</h2></div>
      <button class="btn btn-danger" style="width:100%;" onclick="App.confirmReset()">Reset all data</button>
    </div>
  </div>
  ${renderNav()}`;
}

export function renderReassignCategorySheet(categoryId: string, categoryName: string): string {
  const options = state.categories.filter((c) => c.id !== categoryId);
  return `
    <div class="sheet-head"><h2>Category in use</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
    <div class="sheet-body">
      <p style="color:var(--text-dim);font-size:13.5px;">"${escapeHtml(categoryName)}" is used by existing expenses. Reassign them to another category before deleting.</p>
      <div class="field"><label for="reassignCat">Move expenses to</label>
        <select id="reassignCat">${options.map((c) => `<option value="${c.id}">${escapeHtml(c.name)}</option>`).join('')}</select>
      </div>
      <button class="btn btn-primary btn-block" onclick="App.reassignAndDelete('${categoryId}')">Reassign &amp; delete category</button>
    </div>`;
}

export function renderResetConfirmSheet(): string {
  return `
    <div class="sheet-head"><h2>Reset all data?</h2><button class="sheet-close" onclick="App.closeSheet()" aria-label="Close">✕</button></div>
    <div class="sheet-body">
      <p style="color:var(--text-dim);font-size:13.5px;">This permanently deletes everything stored on this device — trip details, expenses and photos — leaving a blank trip behind.</p>
      <button class="btn btn-danger" style="width:100%;background:rgba(240,85,76,.12);" onclick="App.doReset()">Reset</button>
      <div style="height:10px;"></div>
      <button class="btn btn-ghost" style="width:100%;" onclick="App.closeSheet()">Cancel</button>
    </div>`;
}
